// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import OperatingManualContent from "../OperatingManualContent/OperatingManualContent";
import { WarningIcon } from "src/assets/icons";

type ImportantInformationType = {
  title: string;
};

const ImportantInformation = ({ title }: ImportantInformationType) => {
  const { t } = useTranslation("operating-manual");

  const warnings = [
    {
      text: "Avoid exposing the lenses to sunlight at all times as this can cause damage to the lenses. Damage caused by exposure to sunlight is not covered in the warranty.",
    },
    {
      text: "Results are meant to be reviewed by an optometrist, or ophthalmologist, and used in conjunction with other exams delivered at their discretion as part of a vision health assessment.",
    },
    {
      text: "This test is not intended to be used as a sole screening method; further testing should be completed before diagnosing a condition. It is not intended to be used under any circumstances to treat, diagnose, or drive clinical management.",
    },
    {
      text: "To avoid injury, ensure the patient is seated prior to, and for the entirety of, wearing the headset.",
    },
    {
      text: "To ensure that records are correctly exported to external systems, and to avoid potential misdiagnosis due to mixing-up of patient data, carefully check that the patient identifiers are correct before starting, saving, and exporting the results.",
    },
    {
      text: "A small number of people may experience epilepsy, fainting, dizziness, vomiting, palpitations, and other symptoms caused by flashes and images, even if they have no such medical history. People wearing VR should consult a doctor before use if they have a similar medical history or have ever experienced any of the symptoms listed above.",
    },
    {
      text: "Some people may be allergic to plastic, PU, fabric, and other materials used in this product. Long-term contact with skin may result in symptoms such as redness, swelling, and inflammation. VR users should stop using the product and consult a doctor if they experience any of the symptoms listed above.",
    },
    {
      text: "If patients have a big difference in binocular vision, or myopia, or astigmatism or far-sightedness, it is suggested that they wear glasses to correct their eyesight when using VR headset.",
    },
    {
      text: "Patients should stop using the product immediately if they experience visual abnormalities (diplopia and sight distortion, eye discomfort or pain, etc.), excessive sweating, nausea, vertigo, palpitations, disorientation, loss of balance, etc or other signs of distress.",
    },
    {
      text: "Patients should stop use immediately and consult a doctor if the following symptoms occur:",
      subtext: [
        "Epilepsy seizures, loss of consciousness, convulsions, involuntary movements, dizziness, disorientation, nausea, somnolence, or fatigue.",
        "Eye pain or discomfort, eye fatigue, eye twitching, or visual abnormalities (such as illusion, blurred vision, or diplopia).",
        "Itchy skin, eczema, swelling, irritation or other discomforts.",
        "Excessive sweating, loss of balance, impaired hand-eye coordination, or other similar motion sickness symptoms.",
      ],
    },
    {
      text: "Patients should not operate a motor vehicle, operate machinery, or engage in activities that may have potentially serious consequences until they have fully recovered from these symptoms.",
    },
    { text: "Do not use liquids near the device." },
    {
      text: "The user should read and understand the user manual completely before proceeding to use RVF200.",
    },
    {
      text: "To prevent unauthorized access to patient data, it is required that the computer on which the RVF200 is used is password-protected (using a strong password), free of viruses and malware, equipped with anti-virus software, has a firewall installed and activated; and is updated with the latest security patches.",
    },
    {
      text: "Users should change their portal password every 4 months and not share it with anyone. They should also log out after each session.",
    },
    {
      text: "To avoid malfunction during the procedure, do not use the displaying unit if it is damaged in any way. Only use equipment supplied by RetinaLogik Inc. Do not try to dismantle the equipment in any way.",
    },
    {
      text: "For best results, wipe the lens between uses. Moist cleaning and disinfection wipes can be used to clean the parts of the virtual reality headset (except for the lenses) but should not be dripping to ensure no damage to internal electronics of the displaying unit.",
    },
    {
      text: "Natural light can damage the lenses in the virtual reality headset. Avoid direct sunlight and do not use the system with a window behind the user. Use the system in a room with artificial lighting for best results (not a dark room).",
    },
    {
      text: "The RVF200 is not intended for use in people under 7 years of age.",
    },
    {
      text: "The clinician is solely responsible for obtaining necessary consents for use and disclosure of patient information.",
    },
  ];

  return (
    <OperatingManualContent title={title}>
      <Typography>
        {t(
          "It is important to read this Operating Manual, and the included Instructions for Use, carefully before using the RetinaLogik RVF200. Please note: In this Operating Manual, the terms 'VR' or 'HMD' refer to the virtual reality head-mounted device."
        )}
      </Typography>

      <Box
        id="warnings-and-precautions"
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#E7C43C40",
          borderRadius: "4px",
          padding: "20px",
          gap: "20px",
          mt: "20px",
        }}
      >
        <Typography fontSize="16px" fontWeight={600}>
          {t("Warnings & Precautions")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            mx: "20px",
          }}
        >
          <Typography>
            {t(
              "Failure to observe these warnings and precautions may result in patient injury or damage to the equipment. RetinaLogik Inc. is not responsible for any damage to the equipment, or patient injury, resulting from incorrect use."
            )}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <WarningIcon />
            <Typography fontSize="16px" fontWeight={600}>
              {t("Caution")}
            </Typography>
            <WarningIcon />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              mx: "20px",
            }}
          >
            {warnings.map((warning, warningIndex) => (
              <Box
                key={`warning-${warningIndex}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Typography>
                  {warningIndex + 1}. {t(warning.text)}
                </Typography>

                {warning.subtext && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                      mx: "20px",
                    }}
                  >
                    {warning.subtext?.map((subtext, subtextIndex) => (
                      <Typography key={`subtext-${subtextIndex}`}>
                        • {t(subtext)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </OperatingManualContent>
  );
};

export default ImportantInformation;
